import React, { useState, useEffect, useCallback } from 'react';
import { FaCalendarCheck, FaPaperPlane, FaEye, FaPlus } from 'react-icons/fa';
import api, { errorMessage } from '../api';
import {
  PageShell, Card, Badge, Button, Modal, Field, inputClass, Alert, Loading,
  Table, EmptyState, formatDateTime,
} from './attendance/ui';

const isoWeek = (date) => {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNumber = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayNumber);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil(((target - yearStart) / 86400000 + 1) / 7);
};

/** Weekly attendance sheets submitted by supervisors, alongside the monthly
 *  rural reports. */
const WeeklySubmission = () => {
  const now = new Date();
  const [submissions, setSubmissions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [composing, setComposing] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [draft, setDraft] = useState({
    week: isoWeek(now), year: now.getFullYear(),
    location: '', department: '', rows: [],
  });

  const load = useCallback(async () => {
    setError('');
    try {
      const [submissionRes, locationRes, departmentRes] = await Promise.all([
        api.get('weekly-submissions/'),
        api.get('locations/'),
        api.get('departments/'),
      ]);
      setSubmissions(submissionRes.data);
      setLocations(locationRes.data);
      setDepartments(departmentRes.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load weekly submissions.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadStaff = async (locationId) => {
    setDraft((current) => ({ ...current, location: locationId, rows: [] }));
    if (!locationId) return;
    try {
      const { data } = await api.get('employees/', { params: { location: locationId } });
      setDraft((current) => ({
        ...current,
        rows: data.filter((employee) => employee.status === 'active').map((employee) => ({
          employeeId: employee.employeeId,
          employeeName: employee.name,
          daysPresent: 0, daysAbsent: 0, lateDays: 0, leaveDays: 0, remarks: '',
        })),
      }));
    } catch (err) {
      setError(errorMessage(err, 'Could not load staff for this location.'));
    }
  };

  const setCell = (index, key, value) =>
    setDraft((current) => ({
      ...current,
      rows: current.rows.map((row, position) =>
        position === index ? { ...row, [key]: value } : row),
    }));

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('weekly-submissions/', {
        week: Number(draft.week),
        year: Number(draft.year),
        location: draft.location || undefined,
        department: draft.department || undefined,
        status: 'submitted',
        data: draft.rows.map((row) => ({
          ...row,
          daysPresent: Number(row.daysPresent) || 0,
          daysAbsent: Number(row.daysAbsent) || 0,
          lateDays: Number(row.lateDays) || 0,
          leaveDays: Number(row.leaveDays) || 0,
        })),
      });
      setComposing(false);
      setNotice(`Week ${draft.week} sheet submitted.`);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not submit the weekly sheet.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageShell title="Weekly Attendance Sheets"><Loading /></PageShell>;

  return (
    <PageShell
      title="Weekly Attendance Sheets"
      subtitle="Week-by-week attendance submitted by supervisors"
      actions={<Button onClick={() => setComposing(true)}><FaPlus /> New weekly sheet</Button>}
    >
      <Alert>{error}</Alert>
      <Alert tone="success">{notice}</Alert>

      <Card title={`Submissions (${submissions.length})`}>
        {submissions.length === 0 ? (
          <EmptyState icon={<FaCalendarCheck />} title="No weekly sheets yet"
            hint="Supervisors submit a sheet for each week here." />
        ) : (
          <Table
            columns={['Week', 'Year', 'Location', 'Department', 'Staff', 'Submitted by', 'Submitted', 'Status', '']}
            rows={submissions}
            renderRow={(submission) => (
              <tr key={submission.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="py-3 pr-4 font-medium text-gray-800">Week {submission.week}</td>
                <td className="py-3 pr-4">{submission.year}</td>
                <td className="py-3 pr-4 whitespace-nowrap">{submission.location || '—'}</td>
                <td className="py-3 pr-4 whitespace-nowrap">{submission.department || '—'}</td>
                <td className="py-3 pr-4">{submission.data?.length || 0}</td>
                <td className="py-3 pr-4 whitespace-nowrap">{submission.submittedBy}</td>
                <td className="py-3 pr-4 whitespace-nowrap text-xs">{formatDateTime(submission.submittedDate)}</td>
                <td className="py-3 pr-4"><Badge value={submission.status} /></td>
                <td className="py-3 pr-4">
                  <button onClick={() => setViewing(submission)}
                    className="p-2 text-gray-400 hover:text-[#15A857] hover:bg-green-50 rounded-lg" title="View">
                    <FaEye />
                  </button>
                </td>
              </tr>
            )}
          />
        )}
      </Card>

      <Modal isOpen={composing} onClose={() => setComposing(false)}
        title="Submit weekly attendance sheet" size="max-w-4xl">
        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <Field label="Location *">
              <select required value={draft.location} className={inputClass}
                onChange={(event) => loadStaff(event.target.value)}>
                <option value="">Select a location…</option>
                {locations.map((item) => (
                  <option key={item.id} value={item.id}>{item.name} ({item.type})</option>
                ))}
              </select>
            </Field>
            <Field label="Department">
              <select value={draft.department} className={inputClass}
                onChange={(event) => setDraft({ ...draft, department: event.target.value })}>
                <option value="">All departments</option>
                {departments.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </Field>
            <Field label="ISO week">
              <input type="number" min="1" max="53" value={draft.week} className={inputClass}
                onChange={(event) => setDraft({ ...draft, week: event.target.value })} />
            </Field>
            <Field label="Year">
              <select value={draft.year} className={inputClass}
                onChange={(event) => setDraft({ ...draft, year: event.target.value })}>
                {Array.from({ length: 4 }, (_, index) => now.getFullYear() - index)
                  .map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </Field>
          </div>

          {draft.rows.length === 0 ? (
            <Alert tone="info">Choose a location to load its staff list.</Alert>
          ) : (
            <div className="overflow-x-auto border border-gray-100 rounded-xl">
              <table className="w-full min-w-[700px] text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-gray-500">
                    {['Staff', 'Present', 'Absent', 'Late', 'Leave', 'Remarks'].map((column) => (
                      <th key={column} className="py-2.5 px-3 font-semibold">{column}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {draft.rows.map((row, index) => (
                    <tr key={row.employeeId} className="border-t">
                      <td className="py-2 px-3">
                        <div className="font-medium text-gray-800 whitespace-nowrap">{row.employeeName}</div>
                        <div className="text-xs text-gray-500 font-mono">{row.employeeId}</div>
                      </td>
                      {['daysPresent', 'daysAbsent', 'lateDays', 'leaveDays'].map((key) => (
                        <td key={key} className="py-2 px-3">
                          <input type="number" min="0" max="7" value={row[key]}
                            onChange={(event) => setCell(index, key, event.target.value)}
                            className="w-16 px-2 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#15A857]" />
                        </td>
                      ))}
                      <td className="py-2 px-3">
                        <input value={row.remarks} onChange={(event) => setCell(index, 'remarks', event.target.value)}
                          className="w-full min-w-[120px] px-2 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#15A857]" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex gap-3">
            <Button type="submit" disabled={saving || draft.rows.length === 0} className="flex-1">
              <FaPaperPlane /> {saving ? 'Submitting…' : 'Submit sheet'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setComposing(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!viewing} onClose={() => setViewing(null)}
        title={viewing ? `Week ${viewing.week}, ${viewing.year} · ${viewing.location || 'All locations'}` : ''}
        size="max-w-3xl">
        {viewing && (
          <Table
            columns={['Staff ID', 'Name', 'Present', 'Absent', 'Late', 'Leave', 'Remarks']}
            rows={viewing.data || []}
            empty="This sheet contains no staff rows."
            renderRow={(row) => (
              <tr key={row.employeeId} className="border-b last:border-0">
                <td className="py-2.5 pr-4 font-mono text-xs">{row.employeeId}</td>
                <td className="py-2.5 pr-4 font-medium text-gray-800 whitespace-nowrap">{row.employeeName}</td>
                <td className="py-2.5 pr-4 text-green-700 font-semibold">{row.daysPresent}</td>
                <td className="py-2.5 pr-4 text-red-600">{row.daysAbsent}</td>
                <td className="py-2.5 pr-4 text-amber-600">{row.lateDays ?? '—'}</td>
                <td className="py-2.5 pr-4 text-purple-600">{row.leaveDays ?? '—'}</td>
                <td className="py-2.5 pr-4 text-gray-500">{row.remarks || '—'}</td>
              </tr>
            )}
          />
        )}
      </Modal>
    </PageShell>
  );
};

export default WeeklySubmission;
